import { StatusCodes } from 'http-status-codes';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { ChangeStockTransactionDto, QuantityGetDtoToStockTransactionAddDto } from 'svila-common';
import InventoryApi from '~/src/services/api/InventoryApi';
import { Distinct, ProductX, ReserveBlockStatus, UnitType } from '~/src/types/Misc';

interface BufferTransaction {
  singleCuUnits: number | null;
  singleDpUnits: number | null;
  fullDuUnits: number | null;
  tempId: string;
}

interface returnTypes {
  itemData: ProductX;
  setItemData: React.Dispatch<React.SetStateAction<ProductX>>;
  setQuantity: (quantityId: string, type: UnitType, amount: number) => void;
  bufferTransactions: BufferTransaction[];
  clearAllUnits: (tempId: string) => void;
  clearUnitFromBufferTransaction: (tempId: string, type: UnitType) => void;
  clearAllBufferTransactions: () => void;
  prepareDtoSendAndUpdateScreen: () => void;
  status: ReserveBlockStatus;
  duReserved: number;
  dpReserved: number;
  cuReserved: number;
  duReservePreview: number;
  dpReservePreview: number;
  cuReservePreview: number;
  loading: boolean;
}

export default function useReserver(productData: ProductX, sellOrderId: string): returnTypes {
  const [itemData, setItemData] = useState<ProductX>(productData);
  const [bufferTransactions, setBufferTransactions] = useState<BufferTransaction[]>([]);
  const [status, setStatus] = useState<ReserveBlockStatus>('no-changes');
  const [duReserved, setDuReserved] = useState<number>(0);
  const [dpReserved, setDpReserved] = useState<number>(0);
  const [cuReserved, setCuReserved] = useState<number>(0);
  const [duReservePreview, setDuReservePreview] = useState<number>(0);
  const [dpReservePreview, setDpReservePreview] = useState<number>(0);
  const [cuReservePreview, setCuReservePreview] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const setQuantity = (quantityId: string, type: UnitType, amount: number): void => {
    const distinct = getDistinct(quantityId, itemData);
    const transaction = getBufferTransaction(quantityId, bufferTransactions);
    const singleDuUnits = type === 'du' ? amount : null;
    const singleDpUnits = type === 'dp' ? amount : null;
    const singleCuUnits = type === 'cu' ? amount : null;
    if (transaction) {
      // Update buffer transaction
      updateBufferTransaction(quantityId, type, amount);
    } else {
      if (distinct) {
        addBufferTransactions(quantityId, singleCuUnits, singleDpUnits, singleDuUnits);
      }
    }
  };

  const getDistinct = (quantityId: string, data: ProductX): Distinct | undefined => {
    const distinct = data.distincts.find((variation) => variation.availableQuantities.tempId === quantityId);
    return distinct;
  };

  const getBufferTransaction = (
    bufferTransactionId: string,
    data: BufferTransaction[],
  ): BufferTransaction | undefined => {
    const res = data.find((trans) => trans.tempId === bufferTransactionId);
    return res;
  };

  const addBufferTransactions = (
    tempId: string,
    singleCuUnits: number | null,
    singleDpUnits: number | null,
    fullDuUnits: number | null,
  ): void => {
    setStatus('unsaved');
    const newBufferTransaction = {
      tempId,
      singleCuUnits,
      singleDpUnits,
      fullDuUnits,
    };
    const combinedBufferTransactions = [...bufferTransactions, newBufferTransaction];
    setBufferTransactions(combinedBufferTransactions);
  };

  const updateBufferTransaction = (tempId: string, type: UnitType, amount: number): void => {
    const bufferTransaction = getBufferTransaction(tempId, bufferTransactions);
    if (bufferTransaction) {
      const newBufferTransaction: BufferTransaction = {
        tempId,
        fullDuUnits: bufferTransaction.fullDuUnits,
        singleDpUnits: bufferTransaction.singleDpUnits,
        singleCuUnits: bufferTransaction.singleCuUnits,
      };
      switch (type) {
        case 'du':
          newBufferTransaction.fullDuUnits = amount;
          break;
        case 'dp':
          newBufferTransaction.singleDpUnits = amount;
          break;
        case 'cu':
          newBufferTransaction.singleCuUnits = amount;
          break;
      }
      const cloneBufferTransactions = [...bufferTransactions];
      const index = cloneBufferTransactions.findIndex((i) => i.tempId === tempId);
      cloneBufferTransactions.splice(index, 1, newBufferTransaction);
      setBufferTransactions(cloneBufferTransactions);
    }
  };

  const clearAllUnits = (tempId: string): void => {
    const cloneBufferTransactions = [...bufferTransactions];
    const index = cloneBufferTransactions.findIndex((i) => i.tempId === tempId);
    cloneBufferTransactions.splice(index, 1);
    setBufferTransactions(cloneBufferTransactions);
    if (cloneBufferTransactions.length === 0) {
      setStatus('no-changes');
    }
  };

  const clearUnitFromBufferTransaction = (tempId: string, type: UnitType): void => {
    const bufferTransaction = getBufferTransaction(tempId, bufferTransactions);
    if (bufferTransaction) {
      const newBufferTransaction: BufferTransaction = { ...bufferTransaction };
      switch (type) {
        case 'du':
          newBufferTransaction.fullDuUnits = null;
          break;
        case 'dp':
          newBufferTransaction.singleDpUnits = null;
          break;
        case 'cu':
          newBufferTransaction.singleCuUnits = null;
          break;
      }
      if (
        newBufferTransaction.singleCuUnits === null &&
        newBufferTransaction.singleDpUnits === null &&
        newBufferTransaction.fullDuUnits === null
      ) {
        clearAllUnits(tempId);
      } else {
        const cloneBufferTransactions = [...bufferTransactions];
        const index = cloneBufferTransactions.findIndex((i) => i.tempId === tempId);
        cloneBufferTransactions.splice(index, 1, newBufferTransaction);
        setBufferTransactions(cloneBufferTransactions);
      }
    }
  };

  const clearAllBufferTransactions = (): void => {
    setBufferTransactions([]);
    setStatus('no-changes');
  };

  const prepareDtoSendAndUpdateScreen = async (): Promise<void> => {
    // Prepare Dtos
    const dtos: ChangeStockTransactionDto[] = [];
    bufferTransactions.forEach((bf) => {
      const distinct = getDistinct(bf.tempId, itemData);

      if (distinct) {
        const negativeDu = (bf.fullDuUnits ?? 0) - (distinct.reservedQuantities.fullDuUnits ?? 0);
        const negativeDp = (bf.singleDpUnits ?? 0) - (distinct.reservedQuantities.singleDpUnits ?? 0);
        const negativeCu = (bf.singleCuUnits ?? 0) - (distinct.reservedQuantities.singleCuUnits ?? 0);
        if (negativeDu < 0 || negativeDp < 0 || negativeCu < 0) {
          const negativeDto: ChangeStockTransactionDto = {
            formData: QuantityGetDtoToStockTransactionAddDto(distinct.availableQuantities),
            templateData: QuantityGetDtoToStockTransactionAddDto(distinct.availableQuantities),
          };

          negativeDto.formData.fullDuUnits = negativeDu < 0 ? negativeDu * -1 : null;
          negativeDto.formData.singleDpUnits = negativeDp < 0 ? negativeDp * -1 : null;
          negativeDto.formData.singleCuUnits = negativeCu < 0 ? negativeCu * -1 : null;
          negativeDto.formData.sellOrderId = null;
          negativeDto.formData.warehouseId = distinct?.availableQuantities?.warehouse?.id ?? distinct?.reservedQuantities?.warehouse?.id;
          negativeDto.templateData.warehouseId = distinct?.availableQuantities?.warehouse?.id ?? distinct?.reservedQuantities?.warehouse?.id;
          negativeDto.templateData.sellOrderId = sellOrderId;
          dtos.push(negativeDto);
        }

        const positiveDto: ChangeStockTransactionDto = {
          formData: QuantityGetDtoToStockTransactionAddDto(distinct.availableQuantities),
          templateData: QuantityGetDtoToStockTransactionAddDto(distinct.availableQuantities),
        };
        if (negativeDu > 0 || negativeDp > 0 || negativeCu > 0) {
          positiveDto.formData.fullDuUnits = negativeDu > 0 ? negativeDu : null;
          positiveDto.formData.singleDpUnits = negativeDp > 0 ? negativeDp : null;
          positiveDto.formData.singleCuUnits = negativeCu > 0 ? negativeCu : null;
          positiveDto.formData.sellOrderId = sellOrderId;
          positiveDto.formData.warehouseId = distinct?.availableQuantities?.warehouse?.id ?? distinct?.reservedQuantities?.warehouse?.id;;
          positiveDto.templateData.warehouseId = distinct?.availableQuantities?.warehouse?.id ?? distinct?.reservedQuantities?.warehouse?.id;;
          positiveDto.templateData.sellOrderId = null;
          dtos.push(positiveDto);
        }
      }
    });
    // Send

    let success = true;
    setLoading(true);
    for (let i = 0; i < dtos.length; i++) {
      const dto = dtos[i];
      try {
        await InventoryApi.change(dto);
      } catch (error) {
        success = false;
      }
    }

    setLoading(false);
    // uodate dataItem
    updateData(success);
    // Set stuff
    if (success) {
      setStatus('success');
      setBufferTransactions([]);
    } else {
      setStatus('error');
    }
  };

  const updateData = (newStatus: boolean): void => {
    const newData = _.cloneDeep(itemData);

    if (newStatus) {
      bufferTransactions.forEach((bf) => {
        const distinct = newData.distincts.find((v) => v.availableQuantities.tempId === bf.tempId);
        if (distinct !== undefined) {
          const sumDu =
            (distinct.availableQuantities.fullDuUnits ?? 0) + (distinct.reservedQuantities?.fullDuUnits ?? 0);
          distinct.reservedQuantities.fullDuUnits = bf.fullDuUnits ?? 0;
          distinct.availableQuantities.fullDuUnits = sumDu - (bf.fullDuUnits ?? 0);
          const sumCu =
            (distinct.availableQuantities?.singleCuUnits ?? 0) +
            (distinct?.reservedQuantities?.singleCuUnits ?? 0);
          distinct.reservedQuantities.singleCuUnits = bf.singleCuUnits ?? 0;
          distinct.availableQuantities.singleCuUnits = sumCu - (bf.singleCuUnits ?? 0);
          const sumDp =
            (distinct.availableQuantities?.singleDpUnits ?? 0) +
            (distinct?.reservedQuantities?.singleDpUnits ?? 0);
          distinct.reservedQuantities.singleDpUnits = bf.singleDpUnits ?? 0;
          distinct.availableQuantities.singleDpUnits = sumDp - (bf.singleDpUnits ?? 0);
        }
      });
      setItemData(newData);
    }
  };

  useEffect(() => {
    let duSum = 0;
    let dpSum = 0;
    let cuSum = 0;
    itemData.distincts.forEach((distinct) => {
      duSum += distinct.reservedQuantities.fullDuUnits ?? 0;
      dpSum += distinct.reservedQuantities.singleDpUnits ?? 0;
      cuSum += distinct.reservedQuantities.singleCuUnits ?? 0;
    });
    setDuReserved(duSum);
    setDpReserved(dpSum);
    setCuReserved(cuSum);
  }, [itemData]);

  useEffect(() => {
    let duSum = 0;
    let dpSum = 0;
    let cuSum = 0;
    itemData.distincts.forEach((distinct) => {
      const bufferTransaction = bufferTransactions.find((x) => x.tempId == distinct.availableQuantities.tempId);
      if (bufferTransaction) {
        duSum += bufferTransaction.fullDuUnits ?? distinct.reservedQuantities.fullDuUnits ?? 0;
        dpSum += bufferTransaction.singleDpUnits ?? distinct.reservedQuantities.singleDpUnits ?? 0;
        cuSum += bufferTransaction.singleCuUnits ?? distinct.reservedQuantities.singleCuUnits ?? 0;
      } else {
        duSum += distinct.reservedQuantities.fullDuUnits ?? 0;
        dpSum += distinct.reservedQuantities.singleDpUnits ?? 0;
        cuSum += distinct.reservedQuantities.singleCuUnits ?? 0;
      }
    });
    setDuReservePreview(duSum);
    setDpReservePreview(dpSum);
    setCuReservePreview(cuSum);
  }, [bufferTransactions]);

  return {
    itemData,
    setItemData,
    setQuantity,
    bufferTransactions,
    clearAllUnits,
    clearUnitFromBufferTransaction,
    clearAllBufferTransactions,
    prepareDtoSendAndUpdateScreen,
    status,
    duReserved,
    dpReserved,
    cuReserved,
    duReservePreview,
    dpReservePreview,
    cuReservePreview,
    loading,
  };
}
